import type { BoxRef } from "../../../mads";

import { useRnViewJobDisplay } from "../context/ViewJobDisplayContext";

import { useEffect, useRef, useState, type RefObject } from "react";

const MIN_COLLAPSE_SCROLL_OFFSET = 75;
const SCROLL_ROOT_SELECTOR = '[data-rnvj-scroll-root="true"]';

export type DesktopEmbeddedHeaderCollapseState = {
  isCompact: boolean;
  sentinelRef: RefObject<BoxRef | null>;
};

export function getNextCompactState(
  isCompact: boolean,
  scrollTop: number,
  collapseScrollOffset: number,
): boolean {
  if (scrollTop <= 0) {
    return false;
  }

  return isCompact || scrollTop >= collapseScrollOffset;
}

/**
 * Tracks when the existing desktop header reaches its sticky position.
 *
 * Once compact, the header stays compact until the pane returns to scrollTop 0.
 * This hysteresis prevents the compact header's smaller layout from moving the
 * sentinel back into view and repeatedly toggling the state during a scroll.
 */
export function useDesktopEmbeddedHeaderCollapse(): DesktopEmbeddedHeaderCollapseState {
  const sentinelRef = useRef<BoxRef>(null);
  const isCompactRef = useRef(false);
  const [isCompact, setIsCompact] = useState(false);
  const display = useRnViewJobDisplay();
  const isEnabled = display === "DESKTOP_EMBEDDED";

  useEffect(() => {
    if (!isEnabled) {
      isCompactRef.current = false;
      setIsCompact(false);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const sentinel = sentinelRef.current as unknown as HTMLElement | null;
    const scrollRoot = sentinel?.closest<HTMLElement>(SCROLL_ROOT_SELECTOR);
    if (!sentinel || !scrollRoot) {
      isCompactRef.current = false;
      setIsCompact(false);
      return;
    }

    const handleScroll = () => {
      const sentinelOffset =
        scrollRoot.scrollTop +
        sentinel.getBoundingClientRect().top -
        scrollRoot.getBoundingClientRect().top;
      const collapseScrollOffset = Math.max(MIN_COLLAPSE_SCROLL_OFFSET, sentinelOffset);
      const nextIsCompact = getNextCompactState(
        isCompactRef.current,
        scrollRoot.scrollTop,
        collapseScrollOffset,
      );

      if (nextIsCompact !== isCompactRef.current) {
        isCompactRef.current = nextIsCompact;
        setIsCompact(nextIsCompact);
      }
    };

    handleScroll();
    scrollRoot.addEventListener("scroll", handleScroll, { passive: true });

    return () => scrollRoot.removeEventListener("scroll", handleScroll);
  }, [isEnabled]);

  return {
    isCompact: isEnabled && isCompact,
    sentinelRef,
  };
}
